import styled from '@emotion/styled';
import {collection} from '@firebase/firestore';
import {getDocs} from '@firebase/firestore';
import {getDownloadURL, ref as firestoreRef} from '@firebase/storage';
import {AnimatePresence, motion} from 'framer-motion';
import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {useApp} from '../AppProvider';
import VolumeMuted from './Icons/VolumeMuted';
import VolumeUp from './Icons/VolumeUp';

const Wrapper = styled('section')(() => ({
  width: '100%',
  height: '105vh',
  backgroundColor: 'rgb(0,7,31)',
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  '@media (min-width: 768px)': {
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
  },
}));

const LoadingStyled = styled('div')(() => ({
  backgroundColor: 'rgb(0,7,31)',
  color: '#fff',
  height: '105vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const Video = styled(motion.video)(() => ({
  width: '100%',
  height: '105vh',
  objectFit: 'cover',
  position: 'absolute',
  top: 0,
  left: 0,
}));

const Title = styled(motion.h1)(() => ({
  color: '#fff',
  margin: 0,
  padding: 0,
  width: '100%',
  fontSize: 40,
  flex: 'unset',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  webkitLineClamp: 1,
  lineClamp: 1,
  webkitBoxOrient: 'vertical',
  justifyContent: 'flex-end',
  textAlign: 'right',
  '@media (min-width: 768px)': {
    fontSize: 80,
    flex: 1,
    textAlign: 'left',
    justifyContent: 'flex-start',
  },
}));

const ReelsWrapper = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'nowrap',
  gap: '2rem',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-end',
  flex: 1,
  '@media (min-width: 768px)': {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flex: 'unset',
  },
}));

const Bar = styled('div')(() => ({
  width: 200,
  height: 3,
  position: 'relative',
  backgroundColor: 'rgba(255,255,255,0.5)',
}));

const BarIndicator = styled(motion.div)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  backgroundColor: '#fff',
}));

const MuteButton = styled(motion.button)(() => ({
  color: '#fff',
  fontWeight: 'bold',
  backgroundColor: '#333',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
  zIndex: 2,
}));

const MuteButtons = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  gap: '1rem',
  marginTop: 'auto',
  '@media (min-width: 768px)': {
    marginTop: 50,
  },
}));

const Reels = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  zIndex: 1,
  flexWrap: 'wrap',
  color: '#fff',
  paddingLeft: '3rem',
  paddingRight: '3rem',
  paddingTop: '5rem',
  paddingBottom: '5rem',
  '@media (min-width: 768px)': {
    paddingTop: 0,
  },
}));

const VideoItem = styled('div')(() => ({
  cursor: 'pointer',
}));

type Reel = {
  id: string;
  filename: string;
  url: string;
  title: string;
};

const PLAY_TIME = 20000;

let interval: NodeJS.Timeout | null = null;

const Showreels: FC<{handleNavigateProjekte: () => void}> = ({
  handleNavigateProjekte,
}) => {
  const [loading, setLoading] = useState(true);
  const [reels, setReels] = useState<Reel[]>([]);
  const [currentReelIndex, setCurrentReelIndex] = useState(0);
  const {cookiesAccepted, storage, db} = useApp();
  const [muted, setMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancel = false;

    if (cookiesAccepted === undefined || !db || !storage) {
      return;
    }

    getDocs(collection(db, 'Reels'))
      .then(async (querySnapshot) => {
        const newReels: Reel[] = [];

        for (let i = 0; i < querySnapshot.docs.length; i++) {
          const doc = querySnapshot.docs[i];
          const filename = doc.data().filename;
          const title = doc.data().title;
          const id = doc.id;
          if (storage) {
            try {
              const url = await getDownloadURL(
                firestoreRef(storage, `Reels/${filename}`)
              );
              newReels.push({id, filename, url, title});
            } catch {}
          }
        }

        if (!cancel) {
          setReels(newReels);
        }
      })
      .finally(() => {
        if (!cancel) {
          setLoading(false);
        }
      });

    return () => {
      cancel = true;
    };
  }, [cookiesAccepted, db, storage]);

  useEffect(() => {
    interval = setInterval(() => {
      setCurrentReelIndex((currentReelIndex + 1) % reels.length);
    }, PLAY_TIME);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [currentReelIndex, reels.length]);

  const currentUrl = useMemo(() => {
    if (!reels.length) {
      return '';
    }
    if (currentReelIndex === -1) {
      return '';
    }
    if (reels[currentReelIndex]) {
      return reels[currentReelIndex].url;
    }
    return '';
  }, [currentReelIndex, reels]);

  const currentTitle = useMemo(() => {
    if (!reels.length) {
      return '';
    }
    if (currentReelIndex === -1) {
      return '';
    }
    return reels[currentReelIndex].title;
  }, [currentReelIndex, reels]);

  const handleMuteToggle = () => {
    setMuted((prev) => !prev);
  };

  const handleVideoItemClick = (index: number) => {
    setCurrentReelIndex(index);
    if (interval) {
      clearInterval(interval);
    }
    interval = setInterval(() => {
      setCurrentReelIndex((currentReelIndex + 1) % reels.length);
    }, PLAY_TIME);
  };

  if (loading) {
    return <LoadingStyled>loading...</LoadingStyled>;
  }

  if (!reels.length) {
    return null;
  }

  return (
    <Wrapper ref={wrapperRef} id={'showreels'}>
      <AnimatePresence>
        <Video
          key={currentReelIndex}
          ref={videoRef}
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          autoPlay
          loop
          muted={muted}
          playsInline
        >
          <source src={currentUrl} type="video/mp4" />
        </Video>
      </AnimatePresence>
      <Reels>
        <Title>{currentTitle}</Title>
        <ReelsWrapper>
          {reels.map((reel, index) => (
            <VideoItem
              key={reel.id}
              onClick={() => handleVideoItemClick(index)}
            >
              <p>{reel.title}</p>
              <Bar>
                {index === currentReelIndex && (
                  <BarIndicator
                    animate={{
                      width: '100%',
                    }}
                    transition={{
                      duration: PLAY_TIME / 1000,
                    }}
                  />
                )}
              </Bar>
            </VideoItem>
          ))}
        </ReelsWrapper>
        <MuteButtons>
          <MuteButton
            whileTap={{scale: 0.9}}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            type={'button'}
            onClick={handleNavigateProjekte}
          >
            Click für mehr
          </MuteButton>
          <MuteButton
            whileTap={{scale: 0.9}}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            type={'button'}
            onClick={handleMuteToggle}
            style={{bottom: '12rem'}}
          >
            {muted && (
              <VolumeMuted style={{width: 20, height: 20, fill: '#fff'}} />
            )}
            {!muted && (
              <VolumeUp style={{width: 20, height: 20, fill: '#fff'}} />
            )}
          </MuteButton>
        </MuteButtons>
      </Reels>
    </Wrapper>
  );
};

export default Showreels;
